import { ApiError } from '../../../shared/services/apiClient';

type Translate = (key: string, options?: { ns?: string; defaultValue?: string }) => unknown;

export type TaskValidationItem = {
  label?: string;
  message: string;
};

const FIELD_LABEL_KEYS: Record<string, string> = {
  duedate: 'dueDate',
  duetime: 'dueTime',
  title: 'task',
  description: 'description',
  priority: 'priorityLabel',
  recurrence: 'recurrenceLabel',
  status: 'statusLabel',
  labels: 'labelsLabel',
  assignedtouserid: 'assigneeLabel',
  assignedtoai: 'aiAgent',
  mentions: 'aiAgent',
  categoryid: 'categoryLabel',
  contactid: 'contact',
  assetid: 'asset',
  requeueairun: 'aiAgent',
};

const ISSUE_MESSAGES: Record<string, { key: string; defaultValue: string }> = {
  'due date must be today or later': {
    key: 'validationDueDateFuture',
    defaultValue: 'Choose today or a later date.',
  },
  'title is required': {
    key: 'titleRequired',
    defaultValue: 'Summary is required.',
  },
  'title length must be between 1 and 200': {
    key: 'validationTitleLength',
    defaultValue: 'Use between 1 and 200 characters.',
  },
  'description length must be between 0 and 4000': {
    key: 'validationDescriptionLength',
    defaultValue: 'Use 4,000 characters or fewer.',
  },
  'priority is invalid': {
    key: 'validationPriority',
    defaultValue: 'Choose a priority.',
  },
  'recurrence is invalid': {
    key: 'validationRecurrence',
    defaultValue: 'Choose how often this task repeats.',
  },
  'status is invalid': {
    key: 'validationStatus',
    defaultValue: 'Choose a valid status.',
  },
  'labels count must be 20 or fewer': {
    key: 'validationLabelsCount',
    defaultValue: 'Use 20 labels or fewer.',
  },
  'each label length must be between 1 and 64': {
    key: 'validationLabelLength',
    defaultValue: 'Each label must be between 1 and 64 characters.',
  },
  'task category cannot be changed after creation': {
    key: 'validationCategoryLocked',
    defaultValue: 'Category cannot be changed after the task is created.',
  },
  'ai task runs are turned off for this company': {
    key: 'validationAiTasksDisabled',
    defaultValue: 'AI task runs are turned off for this company.',
  },
  'this ai task is already queued or running': {
    key: 'validationAiTaskAlreadyRunning',
    defaultValue: 'This AI task is already queued or running.',
  },
};

const issueKey = (issue: string) => issue.trim().replace(/[.]+$/, '').toLowerCase();

const translate = (t: Translate, key: string, defaultValue?: string) => {
  const value = t(key, { ns: 'tasks', defaultValue });
  return typeof value === 'string' && value.trim() ? value : (defaultValue ?? key);
};

const fieldLabel = (field: string, t: Translate) => {
  const key = FIELD_LABEL_KEYS[field.replace(/[\s._]/g, '').toLowerCase()];
  return key ? translate(t, key) : '';
};

const friendlyIssue = (issue: string, t: Translate) => {
  const mapped = ISSUE_MESSAGES[issueKey(issue)];
  if (mapped) {
    return translate(t, mapped.key, mapped.defaultValue);
  }

  const trimmed = issue.trim();
  if (!trimmed) {
    return '';
  }

  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

const stripValidationSummary = (message: string) =>
  message.replace(/^one or more fields are invalid\.?\s*/i, '').trim();

const parseMessage = (message: string): Array<{ field: string; issue: string }> => {
  const cleaned = stripValidationSummary(message);
  if (!cleaned) {
    return [];
  }

  return cleaned.split(/\s*;\s*/).map((part) => {
    const match = part.match(/^([A-Za-z][\w]*)\s*:\s*(.+)$/);
    if (!match) {
      return { field: '', issue: part };
    }

    return { field: match[1], issue: match[2] };
  });
};

export const taskValidationItems = (error: unknown, t: Translate): TaskValidationItem[] => {
  const details = error instanceof ApiError && error.details.length > 0
    ? error.details
    : parseMessage(error instanceof Error ? error.message : String(error ?? ''));

  return details
    .map((detail) => ({
      label: detail.field ? fieldLabel(detail.field, t) : undefined,
      message: friendlyIssue(detail.issue, t),
    }))
    .filter((item) => item.message);
};
